/* eslint-disable @nx/enforce-module-boundaries */
import { Component, Inject, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { Driver } from '../../../common/models/Driver';
import { DriverService } from '../../../api/services/driver.service';
import {
  LoaderIndicatorDirective,
  LoaderService,
  NotificationService,
} from '@t-llevo/common-ui';
import { finalize, forkJoin, map, switchMap, tap } from 'rxjs';
import { EditPersonalFormComponent } from './edit-personal-form/edit-personal-form.component';
import { EditVehicleFormComponent } from './edit-vehicle-form/edit-vehicle-form.component';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';
import { DialogHeaderComponent } from 'admin-drivers/src/app/common/dialog/components/dialog-header/dialog-header.component';
import { VehicleTypesService } from 'admin-drivers/src/app/api/services/vehicle-types.service';
import { EditLicensesFormComponent } from './edit-licenses-form/edit-licenses-form.component';
import { HttpErrorResponse } from '@angular/common/http';
import { UploadImageService } from 'admin-drivers/src/app/api/services/upload-image.service';
import { emailValidators } from 'admin-drivers/src/app/common/validators';
import { toSignal } from '@angular/core/rxjs-interop';
import { EditForm } from '../../models/EditForm';
import { LicenseCategoryService } from 'admin-drivers/src/app/api/services/license-category.service';

@Component({
  selector: 't-llevo-driver-edit',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatExpansionModule,
    EditPersonalFormComponent,
    EditVehicleFormComponent,
    EditLicensesFormComponent,
    LoaderIndicatorDirective,
    DialogHeaderComponent,
  ],
  templateUrl: './driver-edit.component.html',
  providers: [LoaderService],
  styles: `
    @import '../../../utils/styles/baseDialog.scss';
    .bg-gray{
      .mat-mdc-dialog-surface{
        background: #EEE;
      }
    }
  
    mat-dialog-actions{
      background: #FFF;
    }
  `,
})
export class DriverEditComponent {
  private driverSrv = inject(DriverService);
  private loaderSrv = inject(LoaderService);
  private notifSrv = inject(NotificationService);
  private vehicleTypesSrv = inject(VehicleTypesService);
  private uploadImgSrv = inject(UploadImageService);
  private licenseCatSrv = inject(LicenseCategoryService)

  private licensesImages: Blob[] | null = null;
  private driverImage: Blob | null = null;
  loading = toSignal(this.loaderSrv.loaderState$);

  frm = this._fb.group({
    personal: this._fb.group({
      name: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', emailValidators],
      phone: ['', [Validators.required, Validators.pattern(/[0-9]{8}/)]],
      birthday: ['', Validators.required],
      dni: [
        '',
        [Validators.required, Validators.pattern(/^\d{11}$|^[A-Za-z]\d{6}$/)],
      ],
      photo: this._fb.control<File | null>(null, [Validators.required]),
    }),
    licenses: this._fb.array<
      FormGroup<{
        categories: FormControl<string[]>;
        licenseNumber: FormControl<string>;
        expeditionDate: FormControl<string>;
        expiredYear: FormControl<number>;
        image: FormControl<File | null>;
      }>
    >([]),
    vehicle: this._fb.group({
      vehicleType: ['', Validators.required],
      mark: ['', Validators.required],
      model: ['', Validators.required],
      color: ['', Validators.required],
      plate: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z]{1}[0-9]{5}$/)],
      ],
    }),
  });

  constructor(
    public dialogRef: MatDialogRef<DriverEditComponent>,
    private _fb: NonNullableFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: Driver
  ) {
    this.dialogRef.afterOpened().subscribe(() => {
      this.loadingData()
        .pipe(
          tap(() => {
            this.assignPersonalData();
            this.assignLicenseData();
            this.assignVehicleData();
          }),
          switchMap(() => this.loadImages()),
          tap(() => this.assignImages())
        )
        .pipe(finalize(() => this.loaderSrv.hide()))
        .subscribe();
    });
  }

  loadingData() {
    this.loaderSrv.show();
    return forkJoin({
      categories: this.licenseCatSrv.getCategories(),
      vehicleTypes: this.vehicleTypesSrv.getVehicleTypes(),
    });
  }

  loadImages() {
    return forkJoin({
      driverImage: this.uploadImgSrv.getImageByUrl(this.data.photo),
      licensesImages: this.uploadImgSrv.getImagesByUrls(
        this.data.licenses.map((lic) => lic.image)
      ),
    }).pipe(
      map((data) => {
        this.licensesImages = data.licensesImages;
        this.driverImage = data.driverImage;
      })
    );
  }

  private assignPersonalData() {
    this.personal.controls['birthday'].setValue(String(this.data.birthday));
    this.personal.controls['dni'].setValue(this.data.ci);
    this.personal.controls['lastname'].setValue(this.data.user.lastname);
    this.personal.controls['name'].setValue(this.data.user.firstname);
    this.personal.controls['email'].setValue(this.data.user.email);
    this.personal.controls['phone'].setValue(this.data.user.phone);
  }

  private assignLicenseData() {
    for (let i = 0; i < this.data.licenses.length; i++) {
      const formGroup = this._fb.group({
        categories: [
          this.data.licenses[i].categories.map((cat) => cat.id),
          Validators.required,
        ],
        licenseNumber: [
          this.data.licenses[i].licenseNumber,
          [Validators.required,/* Validators.pattern(/^[a-zA-Z]{1}[0-9]{6}$/)*/],

        ],
        expeditionDate: [
          this.data.licenses[i].expeditionDate,
          Validators.required,
        ],
        expiredYear: [
          this.data.licenses[i].expiredYear,
          [
            Validators.required,
            /*Validators.min(new Date().getFullYear()),*/
            Validators.pattern(/^[0-9]{4}$/),
          ],
        ],
        image: this._fb.control<File | null>(
          null,
          [Validators.required]
        ),
      });

      this.licenses.push(formGroup);
    }
  }

  private assignVehicleData() {
    this.vehicle.controls['vehicleType'].setValue(
      this.data.vehicle.vehicleType.id
    );
    this.vehicle.controls['mark'].setValue(this.data.vehicle.mark);
    this.vehicle.controls['model'].setValue(this.data.vehicle.model);
    this.vehicle.controls['color'].setValue(this.data.vehicle.color);
    this.vehicle.controls['plate'].setValue(this.data.vehicle.plate);
  }

  private assignImages() {
    const splitedImageName = this.data.photo.split('/');
    const imageName = splitedImageName[splitedImageName.length - 1];

    this.personal.controls['photo'].setValue(
      new File([this.driverImage as Blob], imageName)
    );

    this.frm.controls['licenses'].controls[0];

    for (let i = 0; i < this.frm.controls['licenses'].length; i++) {
      const splitedImageName = this.data.licenses[i].image.split('/');
      const imageName = splitedImageName[splitedImageName.length - 1];

      this.frm.controls['licenses'].controls[0].controls['image'].setValue(
        new File([this.licensesImages![i]], imageName)
      );
    }
  }

  onSumbit() {
    this.loaderSrv.show();
    const idParams = {
      driverId: this.data.id,
      vehicleId: this.data.vehicle.id,
      userId: this.data.user.id,
    };

    this.driverSrv
      .updateDriver(this.frm.getRawValue() as EditForm, idParams)
      .pipe(finalize(() => this.loaderSrv.hide()))
      .subscribe({
        next: () => {
          this.notifSrv.dispatch('Conductor editado', 'success');
          this.dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this.notifSrv.dispatch(err.error.message, 'error');
        },
      });
  }

  get personal() {
    return this.frm.controls['personal'];
  }

  get licenses() {
    return this.frm.controls['licenses'];
  }

  get vehicle() {
    return this.frm.controls['vehicle'];
  }
}
